import React from 'react'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Pie } from 'react-chartjs-2'
import { ProjectDistributionData } from '@/services/reportService'

ChartJS.register(ArcElement, Tooltip, Legend)

interface ProjectDistributionChartProps {
  data: ProjectDistributionData[]
  loading: boolean
}

const ProjectDistributionChart: React.FC<ProjectDistributionChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-secondary-800 mb-4">專案時間分佈</h3>
        <div className="h-80 bg-secondary-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-secondary-500">載入中...</div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-secondary-800 mb-4">專案時間分佈</h3>
        <div className="h-80 flex items-center justify-center text-secondary-500">
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <p>暫無專案數據</p>
          </div>
        </div>
      </div>
    )
  }

  const chartData = {
    labels: data.map(item => item.projectName),
    datasets: [
      {
        data: data.map(item => item.hours),
        backgroundColor: data.map(item => item.projectColor),
        borderColor: data.map(item => item.projectColor),
        borderWidth: 2,
        hoverBorderWidth: 3,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          generateLabels: function(chart: any) {
            const data = chart.data
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const dataset = data.datasets[0]
                const hours = dataset.data[i]
                const percentage = ((hours / dataset.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1)
                
                return {
                  text: `${label} (${hours}h, ${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: dataset.borderWidth,
                  hidden: false,
                  index: i
                }
              })
            }
            return []
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || ''
            const value = context.parsed
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            
            const wholeHours = Math.floor(value)
            const minutes = Math.round((value - wholeHours) * 60)
            
            let timeStr = ''
            if (minutes === 0) {
              timeStr = `${wholeHours} 小時`
            } else {
              timeStr = `${wholeHours} 小時 ${minutes} 分鐘`
            }
            
            return `${label}: ${timeStr} (${percentage}%)`
          }
        }
      }
    },
  }

  const totalHours = data.reduce((sum, item) => sum + item.hours, 0)

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-secondary-800">專案時間分佈</h3>
        <div className="text-sm text-secondary-600">
          總計: {totalHours.toFixed(1)} 小時
        </div>
      </div>
      
      <div className="h-80">
        <Pie data={chartData} options={options} />
      </div>
      
      {/* 專案詳細列表 */}
      <div className="mt-6">
        <h4 className="text-lg font-medium text-secondary-800 mb-3">專案詳情</h4>
        <div className="space-y-2">
          {data.map((item) => (
            <div key={item.projectId} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: item.projectColor }}
                />
                <span className="font-medium text-secondary-800">{item.projectName}</span>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-secondary-600">
                  {item.hours.toFixed(1)} 小時
                </span>
                <span className="font-medium text-primary-600">
                  {item.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* 統計摘要 */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-lg font-semibold text-blue-600">{data.length}</div>
          <div className="text-sm text-blue-500">參與專案</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-lg font-semibold text-green-600">
            {data.length > 0 ? (totalHours / data.length).toFixed(1) : '0'}h
          </div>
          <div className="text-sm text-green-500">平均工時</div>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="text-lg font-semibold text-purple-600">
            {data.length > 0 ? Math.max(...data.map(item => item.hours)).toFixed(1) : '0'}h
          </div>
          <div className="text-sm text-purple-500">最高工時</div>
        </div>
      </div>
    </div>
  )
}

export default ProjectDistributionChart
